import { SelectionModel } from "@angular/cdk/collections";
import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  model,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatAnchor, MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort, MatSortModule, Sort } from "@angular/material/sort";
import { MatTable, MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ConfirmDialogComponent, ConfirmDialogData } from "@cotecna/dialogs";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { InspectorsService } from "src/app/inspectors/services/inspectors.service";
import { TIMESHEET_ITEM_TYPES } from "src/app/items/static-forms/timesheet-item-types";
import { User } from "src/app/session/models/user";
import { Preset } from "src/app/settings/models/preset";
import {
  TimeManagementSettings,
  TimesheetActivity as PresetActivity,
} from "src/app/settings/models/timesheet-settings";
import { PresetsService } from "src/app/settings/services/presets.service";
import { TimeManagementService } from "src/app/settings/services/time-management-service";

import {
  TeamInspectorCatalog,
  TimesheetActivity,
  TimesheetActivityError,
  TimesheetActivityFilter,
  TimesheetActivityInspectorTeam,
  TimesheetActivityStatus,
} from "../model/timesheet-activity";
import { TimesheetActivitiesService } from "../services/timesheet-activities.service";

@Component({
  selector: "im-timesheet-activities",
  imports: [
    CommonModule,
    MatAnchor,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    MatMenuModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],  
  templateUrl: "./timesheet-activities.component.html",
  styleUrl: "./timesheet-activities.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimesheetActivitiesComponent implements OnInit, AfterViewInit {
  private readonly timesheetActivitiesService = inject(
    TimesheetActivitiesService,
  );
  private readonly inspectorsService = inject(InspectorsService);
  private readonly presetsService = inject(PresetsService);
  private readonly timeManagementService = inject(TimeManagementService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  private readonly snackbar = inject(MatSnackBar);

  public TimesheetActivityError = TimesheetActivityError;

  public isLoading = signal<boolean>(false);
  public isSynching = signal<boolean>(false);
  public currentPage = signal<number>(1);
  private getTimesheetActivitiesBlocked: boolean = false;
  private isScrollRequestOnGoing: boolean = false;
  private lastScrollTop: number = 0;
  private readonly pageSize: number = 20;

  @ViewChild("matTable", { read: ElementRef })
  public matTableElement!: ElementRef<HTMLElement>;
  @ViewChild(MatTable) public table!: MatTable<any>;
  public displayedColumns = signal<string[]>([
    "checkbox",
    "date",
    "inspectionId",
    "inspectionContractLocalNumber",
    "inspector",
    "activity",
    "itemType",
    "numberOfItems",
    "dayShiftHours",
    "nightShiftHours",
    "overtimeHours",
    "doubleShiftHours",
    "status",
    "remarks",
    "actions",
  ]);
  public previousDatasource = signal<TimesheetActivity[]>([]);
  public datasource = signal<TimesheetActivity[]>([]);
  public selection = new SelectionModel<TimesheetActivity>(true, []);

  public inspectorTeams = signal<TimesheetActivityInspectorTeam[]>([]);
  public inspectors = signal<User[]>([]);
  public teamInspectorCatalog = signal<TeamInspectorCatalog[]>([]);
  public inspectorSearchText = signal("");
  public filteredTeamInspectorCatalog = computed(() => {
    const search = this.inspectorSearchText().toLowerCase();
    if (!search) return this.teamInspectorCatalog();
    const matches = this.teamInspectorCatalog().filter((i) =>
      i.name.toLowerCase().includes(search),
    );
    if (matches.length === 0) {
      return this.teamInspectorCatalog().filter((i) => i.name === "All");
    }
    return matches;
  });

  private readonly presets = signal<Preset[]>([]);
  public activities = signal<PresetActivity[]>([]);
  public rowActivityCache = new Map<string, PresetActivity[]>();
  public itemTypes =
    signal<{ id: number; name: string }[]>(TIMESHEET_ITEM_TYPES);
  public statuses = signal<TimesheetActivityStatus[]>([
    TimesheetActivityStatus.All,
    TimesheetActivityStatus.Pending,
    TimesheetActivityStatus.Approved,
    TimesheetActivityStatus.Rejected,
    TimesheetActivityStatus.Submitted,
  ]);
  @ViewChild("tableSort") public matSort!: MatSort;

  public filterFutureDates = (date: Date): boolean => {
    return date < new Date();
  };
  public startDate = model<Date | null>(null);
  public endDate = model<Date | null>(null);
  public inspectionId = model<string>("");
  public selectedTeamInspector = model<TeamInspectorCatalog | null>(null);
  public selectedActivity = model<string>("");
  public selectedStatus = model<TimesheetActivityStatus>(
    TimesheetActivityStatus.All,
  );
  public inspectionContractLocalNumber = model<string>("");
  public sortAsc = model<boolean>(false);

  public readonly timesheetActivityFilter = computed<TimesheetActivityFilter>(
    () => {
      return {
        currentPage: this.currentPage(),
        pageSize: this.pageSize,
        startDate: this.startDate(),
        endDate: this.endDate(),
        inspectionId: this.inspectionId(),
        inspectorEmail: this.getSelectedInspectorEmail(),
        teamId: this.getSelectedTeamId(),
        activity: this.selectedActivity(),
        status: this.selectedStatus(),
        inspectionContractLocalNumber: this.inspectionContractLocalNumber(),
        sortAsc: this.sortAsc(),
      };
    },
  );

  private getSelectedInspectorEmail(): string | undefined {
    const selected = this.selectedTeamInspector();
    return selected?.isTeam ? undefined : selected?.id;
  }

  private getSelectedTeamId(): string {
    const selected = this.selectedTeamInspector();
    return selected?.isTeam ? (selected?.id ?? "") : "";
  }

  public async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    await this.getTimeManagementSettings();
    await this.getTeamInspectorsCatalog();
    await this.getActivitiesCatalog();
    await this.getTimesheetActivities();
    this.isLoading.set(false);
  }

  public ngAfterViewInit() {
    this.setDefaultSorting();
  }

  private setDefaultSorting(): void {
    this.matSort.active = "date";
    this.matSort.direction = "desc";
    this.matSort.sortChange.emit({
      active: "date",
      direction: this.matSort.direction,
    });
  }

  public isRowLocked(row: TimesheetActivity): boolean {
    return row.status === TimesheetActivityStatus.Approved;
  }

  public rowInspectorSearch = new Map<string, string>();

  public displayInspectorName = (
    inspector: TeamInspectorCatalog | null,
  ): string => inspector?.name ?? "";

  public onInspectorAutocompleteClosed(): void {
    const search = this.inspectorSearchText().toLowerCase();
    if (search) {
      const hasMatch = this.teamInspectorCatalog().some(
        (i) => i.name !== "All" && i.name.toLowerCase().includes(search),
      );
      if (!hasMatch) {
        const allOption =
          this.teamInspectorCatalog().find((i) => i.name === "All") ?? null;
        this.selectedTeamInspector.set(allOption);
        this.inspectorSearchText.set(allOption?.name ?? "");
        return;
      }
    }
    this.inspectorSearchText.set(this.selectedTeamInspector()?.name ?? "");
  }

  public getInspectorNameByEmail(email: string): string {
    const inspector = this.inspectors().find((i) => i.email === email);
    return inspector ? `${inspector.name} ${inspector.lastName}` : "";
  }

  public getRowInspectorSearchValue(rowId: string, email: string): string {
    return this.rowInspectorSearch.has(rowId)
      ? this.rowInspectorSearch.get(rowId)!
      : this.getInspectorNameByEmail(email);
  }

  public getFilteredInspectorsForRow(rowId: string): User[] {
    const emptyUser = { email: "", name: "", lastName: "" } as User;
    const search = (this.rowInspectorSearch.get(rowId) ?? "").toLowerCase();
    const all = this.inspectors().filter((i) => i.name !== "All");
    if (!search) return [emptyUser, ...all];
    const matches = all.filter((i) =>
      `${i.name} ${i.lastName}`.toLowerCase().includes(search),
    );
    return matches.length > 0 ? [emptyUser, ...matches] : [emptyUser];
  }

  public onRowInspectorInput(rowId: string, value: string): void {
    this.rowInspectorSearch.set(rowId, value);
  }

  public onRowInspectorFocus(rowId: string): void {
    this.rowInspectorSearch.set(rowId, "");
  }

  public onRowInspectorSelected(
    element: TimesheetActivity,
    email: string,
    i: number,
  ): void {
    element.userEmail = email;
    this.rowInspectorSearch.delete(element.id);
    if (!email) return;
    this.changeAllSelectedRows(element, "userEmail");
    this.checkValidityAndSave(i, "userEmail");
  }

  public onRowInspectorClosed(rowId: string): void {
    this.rowInspectorSearch.delete(rowId);
  }

  public isStatusOptionDisabled(
    elementStatus: TimesheetActivityStatus,
    optionStatus: TimesheetActivityStatus,
  ): boolean {
    if (elementStatus === TimesheetActivityStatus.Approved) {
      return optionStatus !== TimesheetActivityStatus.Rejected;
    }

    if (elementStatus === TimesheetActivityStatus.Submitted) {
      return (
        optionStatus !== TimesheetActivityStatus.Approved &&
        optionStatus !== TimesheetActivityStatus.Rejected
      );
    }

    if (elementStatus === TimesheetActivityStatus.Rejected) {
      return optionStatus !== TimesheetActivityStatus.Approved;
    }

    if (elementStatus === TimesheetActivityStatus.Pending) {
      return (
        optionStatus == TimesheetActivityStatus.Pending ||
        optionStatus == TimesheetActivityStatus.Submitted
      );
    }

    return optionStatus === TimesheetActivityStatus.Submitted;
  }

  private async getTimeManagementSettings(): Promise<void> {
    const timeManagementSettings: TimeManagementSettings =
      await this.timeManagementService.getTimeManagementSettings();
    if (timeManagementSettings.overtimeCalculation != null) {
      this.displayedColumns.update((columns) =>
        columns.filter((column) => column !== "overtimeHours"),
      );
    }
  }

  private async getTeamInspectorsCatalog(): Promise<void> {
    this.inspectors.set(
      (await this.inspectorsService.getAllInspectors()).sort(
        this.sortInspectors(),
      ),
    );

    this.inspectorTeams.set(await this.inspectorsService.getInspectorTeams());

    this.teamInspectorCatalog.set([
      {
        id: "",
        name: "All",
        isTeam: false,
      },
      ...this.inspectorTeams().map((team) => ({
        id: team.id,
        name: team.name,
        isTeam: true,
      })),
      ...this.inspectors().map((inspector) => ({
        id: inspector.email,
        name: `${inspector.name} ${inspector.lastName}`,
        isTeam: false,
      })),
    ]);

    this.selectedTeamInspector.set(
      this.teamInspectorCatalog().find((entry) => entry.name === "All") ?? null,
    );
    this.inspectorSearchText.set(this.selectedTeamInspector()?.name ?? "");
  }

  private sortInspectors(): (a: User, b: User) => number {
    return (a1, a2) => {
      if (a1.name >= a2.name) {
        return 1;
      }

      if (a1.name < a2.name) {
        return -1;
      }

      return 0;
    };
  }

  private async getActivitiesCatalog(): Promise<void> {
    this.presets.set(await this.presetsService.getPresetsDetails());

    let activities: PresetActivity[] = [];
    for (const preset of this.presets()) {
      activities = activities.concat(
        preset.timesheetActivities.map((activity) => {
          return { ...activity, name: preset.name + " - " + activity.name };
        }),
      );
    }

    this.activities.set([this.createEmptyActivity(), ...activities]);
  }

  private createEmptyActivity(): PresetActivity {
    const emptyActivity: PresetActivity = {
      id: "",
      name: "All",
      autoPopulate: false,
      remarksMandatory: false,
    };
    return emptyActivity;
  }

  public emptyCellValue(row: TimesheetActivity, property: string): void {
    (row as any)[property] = "";

    if (property === "activityId") {
      row["activityName"] = "";
      this.changeAllSelectedRows(row, "activityId");
    }

    this.cdr.detectChanges();
  }

  public getActivityValues(row: TimesheetActivity): PresetActivity[] {
    const presetIndex = this.presets().findIndex((preset) =>
      preset.timesheetActivities.some(
        (timesheetActivity) => timesheetActivity.id === row.activityId,
      ),
    );

    if (presetIndex >= 0) {
      if (!this.rowActivityCache.has(row.externalId)) {
        this.setActivityValuesToCache(
          row.externalId,
          this.presets()[presetIndex].timesheetActivities,
        );
      }
      return this.presets()[presetIndex].timesheetActivities;
    }
    return this.getActivityValuesFromCache(row);
  }

  public getActivityValuesFromCache(row: TimesheetActivity): PresetActivity[] {
    if (this.rowActivityCache.has(row.externalId)) {
      return this.rowActivityCache.get(row.externalId) ?? [];
    } else {
      return [];
    }
  }

  public async searchActivityValuesAndAddToCache(
    row: TimesheetActivity,
  ): Promise<void> {
    if (row.externalId && !this.rowActivityCache.has(row.externalId)) {
      const presetId = await this.presetsService.getPresetIdFromExternalId(
        row.externalId,
      );

      const presetIndex = this.presets().findIndex(
        (preset) => preset.id === presetId,
      );

      if (presetIndex >= 0) {
        this.setActivityValuesToCache(
          row.externalId,
          this.presets()[presetIndex].timesheetActivities,
        );
      } else {
        this.setActivityValuesToCache(row.externalId, []);
      }
    }
  }

  private setActivityValuesToCache(id: string, value: PresetActivity[]): void {
    this.rowActivityCache.set(id, value);
  }

  private async getTimesheetActivities(): Promise<void> {
    if (!this.getTimesheetActivitiesBlocked) {
      this.isLoading.set(true);
      const newActivities =
        await this.timesheetActivitiesService.getTimesheetActivities(
          this.timesheetActivityFilter(),
        );

      if (newActivities.length < this.pageSize) {
        this.getTimesheetActivitiesBlocked = true;
      }

      this.isLoading.set(false);
      this.datasource.update((activities) => [...activities, ...newActivities]);
      this.previousDatasource.update((activities) => [
        ...activities,
        ...newActivities.map((row) => ({ ...row })),
      ]);
      this.currentPage.update((current) => current + 1);
    }
  }

  public search(): void {
    this.resetGridState();
    this.getTimesheetActivities();
  }

  public clear(): void {
    this.resetGridState();
    this.clearFilters();
    this.getTimesheetActivities();
  }

  private resetGridState(): void {
    this.datasource.set([]);
    this.previousDatasource.set([]);
    this.currentPage.set(1);
    this.getTimesheetActivitiesBlocked = false;
  }

  private clearFilters(): void {
    this.startDate.set(null);
    this.endDate.set(null);
    this.inspectionId.set("");
    this.selectedTeamInspector.set(
      this.teamInspectorCatalog().find((entry) => entry.name === "All") ?? null,
    );
    this.inspectorSearchText.set(this.selectedTeamInspector()?.name ?? "");
    this.selectedActivity.set("");
    this.selectedStatus.set(TimesheetActivityStatus.All);
    this.inspectionContractLocalNumber.set("");
  }

  public getStatusLabel(status: TimesheetActivityStatus): string {
    switch (status) {
      case TimesheetActivityStatus.All:
        return "All";
      case TimesheetActivityStatus.Pending:
        return "Pending";
      case TimesheetActivityStatus.Approved:
        return "Approved";
      case TimesheetActivityStatus.Rejected:
        return "Rejected";
      case TimesheetActivityStatus.Submitted:
        return "Submitted";
    }
  }

  public onSortChange(sort: Sort): void {
    if (sort.active === "date" && this.datasource().length > 0) {
      this.matSort.active = sort.active;
      this.matSort.direction = sort.direction;
      this.sortAsc.set(this.matSort.direction === "asc");
      this.search();
    }
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.datasource().length;
    return numSelected === numRows;
  }

  public selectionHasDifferentInspectionIds(): boolean {
    if (this.selection.selected.length > 0) {
      const selected = this.selection.selected;
      for (let i = 1; i < selected.length; i++) {
        for (let j = 0; j < i; j++) {
          if (selected[i].externalId !== selected[j].externalId) {
            return true;
          }
        }
      }
    }
    return false;
  }

  public async onTableScroll(event: Event) {
    if (!this.isScrollRequestOnGoing && this.datasource().length > 0) {
      const scrollElement = event.target as HTMLElement;
      const currentScrollTop = scrollElement.scrollTop;
      const isScrollingDown = currentScrollTop > this.lastScrollTop;
      this.lastScrollTop = currentScrollTop;

      if (
        isScrollingDown &&
        scrollElement.scrollHeight - scrollElement.scrollTop <=
          scrollElement.clientHeight + 1
      ) {
        this.isScrollRequestOnGoing = true;
        await this.getTimesheetActivities();
        this.isScrollRequestOnGoing = false;
      }
    }
  }

  public changeAllSelectedRows(row: any, property: string): void {
    if (!this.selection.isEmpty() && this.selection.selected.includes(row)) {
      for (const selectedRow of this.selection.selected) {
        if (
          property === "status" ||
          selectedRow.status !== TimesheetActivityStatus.Approved
        ) {
          (selectedRow as any)[property] = (row as any)[property];

          if (property == "activityId") {
            this.matchActivityIdWithActivityName(row);
          }
        }
      }
    }
  }

  public matchActivityIdWithActivityName(row: any): void {
    const fullActivityName = this.activities().find(
      (activity) => activity.id === row.activityId,
    )?.name;

    row.activityName = fullActivityName?.split("-")[1]?.trim() ?? "";
  }

  public onlyNumbers(event: KeyboardEvent) {
    const pattern = /\d/;
    if (!pattern.test(event.key)) {
      event.preventDefault();
    }
  }

  public preventPaste(event: ClipboardEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  public formatTimeString(row: any, property: string): void {
    const stringInput = row[property].replace(/\D/g, "");
    const stringInputClean = stringInput.replace(/^0+/, "");
    const numericInput: number = +stringInputClean;

    if (
      numericInput > 2359 ||
      (numericInput.toString().length == 2 &&
        stringInput.length == 2 &&
        numericInput > 23)
    ) {
      row[property] = "23H 59m";
    } else {
      switch (stringInput.length) {
        case 0:
          row[property] = `00H 00m`;
          break;
        case 1:
          row[property] = `0${stringInput}H 00m`;
          break;
        case 2:
          row[property] = `${stringInput}H 00m`;
          break;
        case 3:
          if (+stringInput.slice(1, 3) > 59) {
            row[property] = `0${stringInput.slice(0, 1)}H 59m`;
          } else {
            row[property] =
              `0${stringInput.slice(0, 1)}H ${stringInput.slice(1, 3)}m`;
          }
          break;
        case 4:
          if (+stringInput.slice(2, 4) > 59) {
            row[property] = `${stringInput.slice(0, 2)}H 59m`;
          } else {
            row[property] =
              `${stringInput.slice(0, 2)}H ${stringInput.slice(2, 4)}m`;
          }
          break;
        default:
          row[property] = "23H 59m";
          break;
      }
    }
  }

  public addNewTimesheetActivity(): void {
    const newTimesheetActivity = {
      id: "",
      date: "",
      externalId: "",
      userEmail: "",
      activityId: "",
      activityName: "",
      itemType: this.itemTypes().find((item) => item.name === "Item")?.id ?? 0,
      numberOfItems: 0,
      dayShiftHours: "00H 00m",
      overtimeHours: "00H 00m",
      nightShiftHours: "00H 00m",
      doubleShiftHours: "00H 00m",
      status: TimesheetActivityStatus.Pending,
      remarks: "",
      inspectionContractLocalNumber: "",
      isValid: false,
      validityError: "",
    };
    this.addNewRowToDatasource(newTimesheetActivity);
  }

  public cloneTimesheetActivity(timesheetActivity: TimesheetActivity): void {
    const clonedTimesheetActivity: TimesheetActivity = {
      ...timesheetActivity,
      id: "",
      numberOfItems: 0,
      dayShiftHours: "00H 00m",
      overtimeHours: "00H 00m",
      nightShiftHours: "00H 00m",
      doubleShiftHours: "00H 00m",
      activityId: "",
      activityName: "",
      status: TimesheetActivityStatus.Pending,
      remarks: "",
      isValid: false,
      validityError: "",
    };
    this.addNewRowToDatasource(clonedTimesheetActivity);
  }

  private addNewRowToDatasource(row: TimesheetActivity): void {
    this.datasource.update((activities) => [{ ...row }, ...activities]);
    this.previousDatasource.update((activities) => [{ ...row }, ...activities]);
    setTimeout(() => {
      this.matTableElement.nativeElement.scrollTop = 0;
    }, 0);
  }

  public async checkValidityAndSave(
    index: number,
    property: string,
  ): Promise<void> {
    const row = this.datasource()[index];
    const previousRow = this.previousDatasource()[index];

    if ((row as any)[property] !== (previousRow as any)[property]) {
      if (!this.selection.isEmpty() && this.selection.selected.includes(row)) {
        for (const selectedRow of this.selection.selected) {
          const rowIndex: number = this.datasource().findIndex(
            (row) => row.id === selectedRow.id,
          );
          await this.handleSingleRowValidityAndSave(selectedRow, rowIndex);
        }
      } else {
        await this.handleSingleRowValidityAndSave(row, index);
      }

      this.previousDatasource.set(this.datasource().map((row) => ({ ...row })));
    }
  }

  private async handleSingleRowValidityAndSave(
    element: TimesheetActivity,
    index: number,
  ): Promise<void> {
    const isRowValid = this.validateRow(element);
    if (isRowValid) {
      this.datasource()[index].isValid = true;
      this.datasource()[index].validityError = "";
      this.cdr.detectChanges();
      if (this.datasource()[index].id) {
        try {
          this.isSynching.set(true);
          await this.timesheetActivitiesService.updateTimesheetActivity(
            this.datasource()[index],
          );
          this.isSynching.set(false);
        } catch (error) {
          this.datasource()[index].isValid = false;
          if (error === TimesheetActivityError.InvalidDateDueToTimeOff) {
            this.datasource()[index].validityError =
              TimesheetActivityError.InvalidDateDueToTimeOff;
          } else if (
            error === TimesheetActivityError.InvalidHoursMoreThanAllowed
          ) {
            this.datasource()[index].validityError =
              TimesheetActivityError.InvalidHoursMoreThanAllowed;
          } else if (
            error === TimesheetActivityError.RemarkIsMandatory
          ) {
            this.datasource()[index].validityError =
              TimesheetActivityError.RemarkIsMandatory;
          }
          this.cdr.detectChanges();
        }
      } else {
        try {
          this.isSynching.set(true);
          const newActivityId =
            await this.timesheetActivitiesService.saveTimesheetActivity(
              this.datasource()[index],
            );
          this.isSynching.set(false);
          this.datasource()[index].id = newActivityId;
        } catch (error) {
          this.datasource()[index].isValid = false;
          if (error === TimesheetActivityError.InvalidDateDueToTimeOff) {
            this.datasource()[index].validityError =
              TimesheetActivityError.InvalidDateDueToTimeOff;
          } else if (
            error === TimesheetActivityError.InvalidHoursMoreThanAllowed
          ) {
            this.datasource()[index].validityError =
              TimesheetActivityError.InvalidHoursMoreThanAllowed;
          } else if (
            error === TimesheetActivityError.RemarkIsMandatory
          ) {
            this.datasource()[index].validityError =
              TimesheetActivityError.RemarkIsMandatory;
          }
          this.cdr.detectChanges();
        }
      }
    } else {
      this.datasource()[index].isValid = false;
      this.cdr.detectChanges();
    }
  }

  private validateRow(row: TimesheetActivity): boolean {
    if (!row.date) return false;
    if (!row.externalId) return false;
    if (!row.userEmail) return false;
    if (!row.activityId) return false;
    if (!row.activityName) return false;
    return true;
  }

  public async openDeleteConfirmDialog(row: TimesheetActivity): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "delete_outline",
        okButton: {
          text: "Yes",
          color: "warn",
        },
        cancelButton: {
          text: "No",
        },
        title: "Delete Timesheet Activity",
        message: `Are you sure you want to delete this entry?`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });

    const confirmDialogResult = await firstValueFrom(
      confirmDialog.afterClosed(),
    );
    if (confirmDialogResult) {
      if (!this.selection.isEmpty() && this.selection.selected.includes(row)) {
        let hasToShowInfoBanner: boolean = false;
        for (const selectedRow of this.selection.selected) {
          if (
            selectedRow.status !== TimesheetActivityStatus.Approved &&
            selectedRow.id
          ) {
            await this.deleteTimesheetActivityRow(selectedRow);
          } else {
            hasToShowInfoBanner = true;
          }
        }

        if (hasToShowInfoBanner) {
          this.snackbar.open(
            "Approved and newly created activities cannot be deleted. Please, change the status in order to do so.",
            "CLOSE",
            {
              duration: 3000,
              panelClass: "info-snackbar",
            },
          );
        }
      } else {
        await this.deleteTimesheetActivityRow(row);
      }
    }
  }

  private async deleteTimesheetActivityRow(
    row: TimesheetActivity,
  ): Promise<void> {
    try {
      this.isSynching.set(true);
      await this.timesheetActivitiesService.deleteTimesheetActivity(row.id);
    } finally {
      this.removeRowFromSelection(row);
      this.removeRowFromDatasources(row);
      this.isSynching.set(false);
    }
  }

  private removeRowFromSelection(row: TimesheetActivity): void {
    if (!this.selection.isEmpty() && this.selection.selected.includes(row)) {
      this.selection.deselect(row);
    }
  }

  private removeRowFromDatasources(row: TimesheetActivity): void {
    this.datasource.update((timesheetActivities) => {
      return timesheetActivities.filter(
        (timesheetActivity) => timesheetActivity.id !== row.id,
      );
    });
    this.previousDatasource.update((timesheetActivities) => {
      return timesheetActivities.filter(
        (timesheetActivity) => timesheetActivity.id !== row.id,
      );
    });
  }

  public greaterThanAllowedShift(shift: string): boolean {
    const splittedShift = shift.split("H");

    if (+splittedShift[0].trim() > 12) return true;
    else if (
      +splittedShift[0].trim() == 12 &&
      +splittedShift[1].trim().slice(0, 2) > 0
    ) {
      return true;
    }
    return false;
  }
}
